import {Subscription} from "rxjs"
import {AbortUpdateError, ImageUpdateScheduler} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-update-scheduler"
import {getMipLevelSize, HalImageVirtualRef, PageDescriptor} from "@cm/hal"
import {Descriptor, MipLevelRegion, Region, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {
    ImageOpHalOutputImage,
    SubSampleRegionFunction,
    WriteOutputImageRegionFunction,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-output-image"
import {Box2, Box2Like} from "@cm/math"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {
    applyWrapToRegion,
    mergeRegionsInplace,
} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-helpers"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {WebGl2ImageAtlas} from "@cm/webgl2/webgl2-image-atlas"
import {WebGl2ImageVirtual} from "@cm/webgl2/webgl2-image-virtual"
import {ExtUpdateImageOptions, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"

const ALLOW_SUB_SAMPLING = true

export class ImageOpHalOutputImageVirtual extends ImageOpHalOutputImage {
    constructor(ctx: ImageOpContextHal, descriptor: Descriptor) {
        super(ctx, descriptor)
        this._outputImagePtr = this.ctx.createImageVirtual(descriptor)
        this._imageUpdateScheduler = new ImageUpdateScheduler(this._outputImagePtr, this.ctx.logger)
        // this._imageUpdateScheduler.updateAborted.subscribe((pageDescriptors) => this.freePages(pageDescriptors))
        this._subscriptions.push(this._outputImagePtr.ref.beforePageRemoved.subscribe((pageDescriptor) => this.beforePageRemoved(pageDescriptor)))
        this._subscriptions.push(this._outputImagePtr.ref.pageRemoved.subscribe((pageDescriptor) => this.freePagesInternal([pageDescriptor], false)))
    }

    protected override dispose() {
        this._imageUpdateScheduler.dispose()
        this._outputImagePtr?.release()
        this._outputImagePtr = null
        this._subscriptions.forEach((subscription) => subscription.unsubscribe())
        this._subscriptions.length = 0
        this._pageUpdateInfosByPageHash.clear()
        super.dispose()
    }

    setPagePriority(pagePriority: number) {
        this.outputImagePtr.ref.setPagePriority(pagePriority)
    }

    get outputImagePtr(): HalImageVirtualRef {
        if (!this._outputImagePtr) {
            throw new Error("Attempting to access disposed outputImagePtr")
        }
        return this._outputImagePtr
    }

    invalidateRegions(regions: Regions): void {
        // find all pages which are affected by this region (across all mip-levels)
        const pageDescriptorsToFree: PageDescriptor[] = []
        for (const pageUpdateInfo of this._pageUpdateInfosByPageHash.values()) {
            const mipLevelScale = 1 << pageUpdateInfo.pageDescriptor.mipLevel
            const rescaledRegion: Region = {
                x: pageUpdateInfo.pageDescriptor.region.x * mipLevelScale,
                y: pageUpdateInfo.pageDescriptor.region.y * mipLevelScale,
                width: pageUpdateInfo.pageDescriptor.region.width * mipLevelScale,
                height: pageUpdateInfo.pageDescriptor.region.height * mipLevelScale,
            }
            if (regions.some((region) => !Box2.intersect(region, rescaledRegion).isEmpty())) {
                pageDescriptorsToFree.push(pageUpdateInfo.pageDescriptor)
            }
        }
        if (pageDescriptorsToFree.length > 0) {
            this.freePages(pageDescriptorsToFree)
        }
    }

    async getUpdatedHalImage(
        writeRegionFn: WriteOutputImageRegionFunction,
        subSampleRegionFn: SubSampleRegionFunction,
        mipLevelRegion: MipLevelRegion,
        options: ExtUpdateImageOptions,
    ): Promise<RegionLockedHalImageRef> {
        if (mipLevelRegion.width * mipLevelRegion.height > TextureEditorSettings.RegionPixelsWarnLimit) {
            this.ctx.logger.warn(
                `Large region size ${mipLevelRegion.width * mipLevelRegion.height} for mip level ${mipLevelRegion.mipLevel} on object ${this.debugId}`,
                this,
            )
        }

        // wrap the region to the mip level size
        const mipLevel = mipLevelRegion.mipLevel
        const regions = applyWrapToRegion(this.descriptor, mipLevelRegion, mipLevel)

        // if (TextureEditorSettings.EnableFullTrace) {
        //     // sanity check
        //     regionsByMipLevel.forEach((regions, mipLevel) => {
        //         const mipLevelSize = getMipLevelSize(this.descriptor, mipLevel)
        //         regions.forEach((region) => {
        //             if (Box2.intersect(region, Box2.fromSize(mipLevelSize)).isEmpty()) {
        //                 console.warn(
        //                     `Region [${region.x}, ${region.y}, ${region.width}, ${region.height}] is outside of mip level size [${mipLevelSize.width}, ${mipLevelSize.height}]`,
        //                 )
        //             }
        //         })
        //     })
        // }

        this.addRef("ImageOpHalOutputImageVirtual.getUpdatedHalImage") // ensure we are not disposed while an update is pending
        const outputImagePtr = this.outputImagePtr
        try {
            const allPageDescriptors = await this.updateRegions(writeRegionFn, subSampleRegionFn, regions, mipLevel, options)
            const isTemporary = !options.cachingEnabled
            const lockPages = (pageDescriptors: PageDescriptor[]) => {
                this.lockVirtualPages(outputImagePtr, pageDescriptors)
            }
            const unlockPages = (pageDescriptors: PageDescriptor[]) => {
                this.unlockVirtualPages(outputImagePtr, pageDescriptors)
                if (isTemporary) {
                    this.freeVirtualPages(outputImagePtr, pageDescriptors)
                }
            }
            return RegionLockedHalImageRef.fromHalImageVirtualRef(this, mipLevelRegion, allPageDescriptors.flat(), {
                lockPages,
                unlockPages,
            })
        } finally {
            this.release("ImageOpHalOutputImageVirtual.getUpdatedHalImage")
        }
    }

    private async updateRegions(
        writeRegionFn: WriteOutputImageRegionFunction,
        subSampleRegionFn: SubSampleRegionFunction,
        regions: Regions,
        mipLevel: number,
        options?: UpdateImageOptions,
    ): Promise<PageDescriptor[]> {
        const outputImagePtr = this.outputImagePtr

        // Determine all pages needed for the requested regions
        const pageDescriptorByHash = new Map<number, PageDescriptor>()
        for (const region of regions) {
            const pageDescriptors = this.determinePages(region, mipLevel)
            pageDescriptors.forEach((pageDescriptor) => pageDescriptorByHash.set(pageDescriptor.hash, pageDescriptor))
        }
        const allPageDescriptors = Array.from(pageDescriptorByHash.values())
        if (allPageDescriptors.length > TextureEditorSettings.PageDescriptorWarnLimit) {
            this.ctx.logger.warn(`Massive allocation of pages (${allPageDescriptors.length}) for object ${this.debugId}`, this)
        }

        // Determine which pages need updating
        const nonReadyPages = this.determineNonReadyPages(allPageDescriptors)
        const {subSamplingSourcePages} = nonReadyPages

        // Lock all pages before scheduling work so they aren't evicted during computation
        this.lockVirtualPages(outputImagePtr, allPageDescriptors)
        // Also lock the mip-(M-1) source pages used for sub-sampling. The scheduler defers
        // execution via queueMicrotask, so other concurrent getUpdatedHalImage calls can run
        // between here and when executeSubSamplePages actually reads those pages. Without this
        // lock they are eligible for atlas eviction during that window, producing black tiles.
        if (subSamplingSourcePages.length > 0) {
            this.lockVirtualPages(outputImagePtr, subSamplingSourcePages)
        }

        // Collect update infos for already-pending pages, then schedule new work
        const pendingPageUpdateInfos: PageUpdateInfo[] = [...nonReadyPages.pendingPages]

        const schedulePageUpdates = (
            pageDescriptors: PageDescriptor[],
            fn: (outputImageRef: HalImageVirtualRef, pageDescriptors: PageDescriptor[], options?: UpdateImageOptions) => Promise<void>,
        ) => {
            // AbortUpdateError is handled via the abort signal path below; swallow it here so
            // Promise.all doesn't short-circuit and leave pages in an inconsistent state.
            const pending = this._imageUpdateScheduler.scheduleUpdate(pageDescriptors, fn, options?.priority ?? 0, this.debugId).catch((e) => {
                if (!(e instanceof AbortUpdateError)) throw e
            })
            pageDescriptors.forEach((pageDescriptor) => {
                const pageUpdateInfo: PageUpdateInfo = {
                    pageDescriptor,
                    numRequests: 0,
                    pendingRequest: pending,
                }
                pendingPageUpdateInfos.push(pageUpdateInfo)
                this._pageUpdateInfosByPageHash.set(pageDescriptor.hash, pageUpdateInfo)
            })
        }

        if (nonReadyPages.pagesToBeSubSampled.length > 0) {
            schedulePageUpdates(nonReadyPages.pagesToBeSubSampled, this.executeSubSamplePages(subSampleRegionFn))
        }
        if (nonReadyPages.pagesToBeComputed.length > 0) {
            schedulePageUpdates(nonReadyPages.pagesToBeComputed, this.executeWritePages(writeRegionFn))
        }

        // Increment request counts for all pending infos (both existing and newly scheduled)
        const pendingWrites = pendingPageUpdateInfos.map((info) => {
            info.numRequests++
            if (!info.pendingRequest) throw new Error("No pending request")
            return info.pendingRequest
        })

        // Register abort handler only AFTER numRequests has been incremented for all entries.
        // Registering earlier would race with the increment: abort could fire while numRequests is
        // still 0, decrement to -1, and trigger the "numRequests should not be negative" invariant.
        const abortWritePages = () => {
            pendingPageUpdateInfos.forEach((pageUpdateInfo) => {
                pageUpdateInfo.numRequests--
                if (pageUpdateInfo.numRequests < 0) {
                    throw new Error("numRequests should not be negative")
                }
                if (pageUpdateInfo.numRequests === 0) {
                    this.ctx.logger.trace(
                        `Cancelling request for page [${pageUpdateInfo.pageDescriptor.pageIndex.x}, ${pageUpdateInfo.pageDescriptor.pageIndex.y}] on mip level ${pageUpdateInfo.pageDescriptor.mipLevel} for object `,
                        this,
                    )
                    const updateRemoved = this._imageUpdateScheduler.cancelScheduledUpdate(pageUpdateInfo.pageDescriptor)
                    // if it was not removed, the update was already issued and we keep the page
                    if (updateRemoved) {
                        this.freePages([pageUpdateInfo.pageDescriptor])
                    }
                }
            })
        }
        options?.abortSignal?.addEventListener("abort", abortWritePages, {once: true})

        try {
            await Promise.all(pendingWrites)
        } catch (e) {
            options?.abortSignal?.removeEventListener("abort", abortWritePages)
            this.unlockVirtualPages(outputImagePtr, allPageDescriptors)
            if (subSamplingSourcePages.length > 0) {
                this.unlockVirtualPages(outputImagePtr, subSamplingSourcePages)
            }
            throw e
        }

        options?.abortSignal?.removeEventListener("abort", abortWritePages)

        if (options?.abortSignal?.aborted) {
            this.unlockVirtualPages(outputImagePtr, allPageDescriptors)
            if (subSamplingSourcePages.length > 0) {
                this.unlockVirtualPages(outputImagePtr, subSamplingSourcePages)
            }
            throw new AbortUpdateError("User aborted update")
        }

        // Sub-sampling is complete; release the source page locks (target page locks are
        // held by the returned RegionLockedHalImageRef and released by the caller).
        if (subSamplingSourcePages.length > 0) {
            this.unlockVirtualPages(outputImagePtr, subSamplingSourcePages)
        }

        return allPageDescriptors
    }

    private executeSubSamplePages =
        (subSampleRegionFn: SubSampleRegionFunction) => async (outputImageRef: HalImageVirtualRef, pageDescriptors: PageDescriptor[]) => {
            // console.log(`Sub-sampling ${pageDescriptors.length} pages to virtual image for object ${this.debugId}`, this, pageDescriptors)
            try {
                this.subSamplePages(subSampleRegionFn, outputImageRef, pageDescriptors)
                this.resetPendingRequests(pageDescriptors)
            } catch (e) {
                // If writePages throws mid-chunk (e.g. AbortUpdateError), the scheduler's .catch
                // swallows the error so pendingRequest already appears resolved. Pages from
                // unwritten chunks would then be returned as "ready" with empty/black atlas data.
                // Free all pages so they appear MISSING and are recomputed on the next request.
                this.freePages(pageDescriptors)
                throw e
            }
        }

    private executeWritePages =
        (writeRegionFn: WriteOutputImageRegionFunction) =>
        async (outputImageRef: HalImageVirtualRef, pageDescriptors: PageDescriptor[], options?: UpdateImageOptions) => {
            // console.log(`Writing ${pageDescriptors.length} pages to virtual image for object ${this.debugId}`, this, pageDescriptors)
            try {
                await this.writePages(writeRegionFn, outputImageRef, pageDescriptors, options)
                this.resetPendingRequests(pageDescriptors)
            } catch (e) {
                // If writePages throws mid-chunk (e.g. AbortUpdateError), the scheduler's .catch
                // swallows the error so pendingRequest already appears resolved. Pages from
                // unwritten chunks would then be returned as "ready" with empty/black atlas data.
                // Free all pages so they appear MISSING and are recomputed on the next request.
                this.freePages(pageDescriptors)
                throw e
            }
        }

    private resetPendingRequests = (pageDescriptors: PageDescriptor[]) => {
        pageDescriptors.forEach((pageDescriptor) => {
            const info = this._pageUpdateInfosByPageHash.get(pageDescriptor.hash)
            // TODO is this reliable or can this overwrite newer requests?
            if (info) {
                info.pendingRequest = null
            }
        })
    }

    protected determinePages(region: Box2Like, mipLevel: number) {
        return this.outputImagePtr.ref.determinePages(region, mipLevel)
    }

    private determinePageStatus(pageDescriptors: PageDescriptor[]) {
        const missingPages: PageDescriptor[] = []
        const pendingPages: PageUpdateInfo[] = []
        for (const pageDescriptor of pageDescriptors) {
            const info = this._pageUpdateInfosByPageHash.get(pageDescriptor.hash)
            if (!info) {
                missingPages.push(pageDescriptor)
            } else if (info.pendingRequest) {
                pendingPages.push(info)
            }
        }
        return {
            missingPages,
            pendingPages,
        }
    }

    private determineNonReadyPages(pageDescriptors: PageDescriptor[]) {
        const pageStatus = this.determinePageStatus(pageDescriptors)
        const missingPagesByHash = new Map<number, PageDescriptor>(pageStatus.missingPages.map((pageDescriptor) => [pageDescriptor.hash, pageDescriptor]))
        // for all missing pages, check if they can be sub-sampled from the next mip level
        const pagesToBeSubSampled: PageDescriptor[] = []
        // track the source pages actually used for sub-sampling so callers can lock them
        const subSamplingSourcePagesByHash = new Map<number, PageDescriptor>()
        if (pageDescriptors.length > 0) {
            const mipLevel = pageDescriptors[0].mipLevel
            if (ALLOW_SUB_SAMPLING && mipLevel > 0) {
                pageStatus.missingPages.forEach((pageDescriptor) => {
                    const sourceRegion = {
                        x: pageDescriptor.region.x * 2,
                        y: pageDescriptor.region.y * 2,
                        width: pageDescriptor.region.width * 2,
                        height: pageDescriptor.region.height * 2,
                    }
                    const sourcePageDescriptors = this.determinePages(sourceRegion, pageDescriptor.mipLevel - 1)
                    const sourcePageStatus = this.determinePageStatus(sourcePageDescriptors)
                    if (sourcePageStatus.missingPages.length === 0 && sourcePageStatus.pendingPages.length === 0) {
                        // no pages are missing in the next mip-level so this page can be sub-sampled
                        pagesToBeSubSampled.push(pageDescriptor)
                        missingPagesByHash.delete(pageDescriptor.hash)
                        // collect source pages (deduplicated) so the caller can lock them
                        sourcePageDescriptors.forEach((sp) => subSamplingSourcePagesByHash.set(sp.hash, sp))
                    }
                })
            }
        }
        return {
            pendingPages: pageStatus.pendingPages,
            pagesToBeComputed: Array.from(missingPagesByHash.values()),
            pagesToBeSubSampled,
            subSamplingSourcePages: Array.from(subSamplingSourcePagesByHash.values()),
        }
    }

    private mergePageRegions(pageDescriptors: PageDescriptor[]) {
        if (pageDescriptors.length === 0) {
            throw new Error("No page descriptors")
        }
        const pageRegions: (Region & {pageDescriptors: PageDescriptor[]})[] = []
        const mipLevel = pageDescriptors[0].mipLevel
        for (const pageDescriptor of pageDescriptors) {
            if (pageDescriptor.mipLevel !== mipLevel) {
                throw new Error("Mip level mismatch")
            }
            pageRegions.push({
                ...pageDescriptor.region,
                pageDescriptors: [pageDescriptor],
            })
        }
        mergeRegionsInplace(pageRegions, {
            fuseFn: (masterRegion, fusingRegion) => masterRegion.pageDescriptors.push(...fusingRegion.pageDescriptors),
        })

        // make sure the page regions dont become too massive to avoid running out of memory for temporary images
        type PagedRegion = Region & {pageDescriptors: PageDescriptor[]}
        function splitRegionIntoChunks(region: PagedRegion, maxPixels: number): PagedRegion[] {
            const pageSize = WebGl2ImageAtlas.pageSize
            const maxRowsRaw = Math.max(1, Math.floor(maxPixels / region.width))
            // Snap to page-size multiples so chunk boundaries stay page-aligned (required for virtual-to-virtual copies)
            const maxRows = Math.max(pageSize, Math.floor(maxRowsRaw / pageSize) * pageSize)
            if (region.height <= maxRows) {
                return [region]
            }
            const chunks: PagedRegion[] = []
            for (let y = region.y; y < region.y + region.height; y += maxRows) {
                const height = Math.min(maxRows, region.y + region.height - y)
                chunks.push({
                    x: region.x,
                    y,
                    width: region.width,
                    height,
                    pageDescriptors: region.pageDescriptors.filter((pd) => pd.region.y >= y && pd.region.y < y + height),
                })
            }
            return chunks
        }
        const finalPageRegions = pageRegions.flatMap((pageRegion) => splitRegionIntoChunks(pageRegion, TextureEditorSettings.MaxWriteRegionPixels))

        return {
            mipLevel,
            pageRegions: finalPageRegions,
        }
    }

    private subSamplePages(subSampleRegionFn: SubSampleRegionFunction, outputImageRef: HalImageVirtualRef, pageDescriptors: PageDescriptor[]) {
        const start = performance.now()
        const mergedPageRegions = this.mergePageRegions(pageDescriptors)
        for (const pageRegion of mergedPageRegions.pageRegions) {
            subSampleRegionFn(outputImageRef.ref, outputImageRef.ref, {...pageRegion, mipLevel: mergedPageRegions.mipLevel})
        }
        this.ctx.logger.trace(
            `Sub-sampled ${pageDescriptors.length} pages on mip level ${mergedPageRegions.mipLevel} in ${performance.now() - start}ms for object ${this.debugId}`,
            this,
        )
    }

    private async writePages(
        writeRegionFn: WriteOutputImageRegionFunction,
        outputImageRef: HalImageVirtualRef,
        pageDescriptors: PageDescriptor[],
        options?: UpdateImageOptions,
    ) {
        const mergedPageRegions = this.mergePageRegions(pageDescriptors)
        const mipLevelSize = getMipLevelSize(this.descriptor, mergedPageRegions.mipLevel)
        for (const pageRegion of mergedPageRegions.pageRegions) {
            const start = performance.now()
            // if (TextureEditorSettings.EnableFullTrace) {
            //     console.log(
            //         `Starting to write region [${pageRegion.x}, ${pageRegion.y}, ${pageRegion.width}, ${pageRegion.height}] on mip level ${mergedPageRegions.mipLevel} for object ${this.debugId}`,
            //         this,
            //     )
            // }
            // clip the page region to image bounds to avoid out-of-bounds writes, which would cause unnecessary
            // wrap-around fetches from source images
            const clippedPageRegion: Region = {
                ...pageRegion,
                width: Math.min(pageRegion.width, Math.max(0, mipLevelSize.width - pageRegion.x)),
                height: Math.min(pageRegion.height, Math.max(0, mipLevelSize.height - pageRegion.y)),
            }
            await writeRegionFn(outputImageRef.ref, {...clippedPageRegion, mipLevel: mergedPageRegions.mipLevel}, options)
            const webGl2ImageVirtual = outputImageRef.ref
            if (!(webGl2ImageVirtual instanceof WebGl2ImageVirtual)) {
                throw new Error("Expected WebGl2ImageVirtual")
            }
            this.ctx.logger.trace(
                `Completed to write region [${pageRegion.x}, ${pageRegion.y}, ${pageRegion.width}, ${pageRegion.height}] on mip level ${mergedPageRegions.mipLevel} to atlas (${pageRegion.pageDescriptors.length}x${this.descriptor.channels} pages) in ${performance.now() - start}ms for HalImage ${this.debugId}`,
            )
        }
    }

    private beforePageRemoved(_pageDescriptor: PageDescriptor) {}

    private freePages(pageDescriptors: readonly PageDescriptor[]) {
        this.freePagesInternal(pageDescriptors, true)
    }

    private freePagesInternal(pageDescriptors: readonly PageDescriptor[], freeVirtualPages: boolean) {
        pageDescriptors.forEach((pageDescriptor) => this._pageUpdateInfosByPageHash.delete(pageDescriptor.hash))
        if (freeVirtualPages && this._outputImagePtr) {
            this.freeVirtualPages(this._outputImagePtr, pageDescriptors)
        }
    }

    private lockVirtualPages(outputImagePtr: HalImageVirtualRef, pageDescriptors: readonly PageDescriptor[]) {
        outputImagePtr.ref.lockPages(pageDescriptors)
    }

    private unlockVirtualPages(outputImagePtr: HalImageVirtualRef, pageDescriptors: readonly PageDescriptor[]) {
        outputImagePtr.ref.unlockPages(pageDescriptors)

        // free pages which were deferred to be freed (in case this was the last lock)
        const pageDescriptorsToBeFreed = pageDescriptors.filter((pageDescriptor) => this._deferredVirtualPageHashesToBeFreed.has(pageDescriptor.hash))
        if (pageDescriptorsToBeFreed.length > 0) {
            pageDescriptorsToBeFreed.forEach((pageDescriptor) => this._deferredVirtualPageHashesToBeFreed.delete(pageDescriptor.hash))
            this.freeVirtualPages(outputImagePtr, pageDescriptorsToBeFreed)
        }
    }

    private freeVirtualPages(outputImagePtr: HalImageVirtualRef, pageDescriptors: readonly PageDescriptor[]) {
        // only free pages which are not locked anymore; defer the freeing of locked pages
        const nonLockedPageDescriptors: PageDescriptor[] = []
        pageDescriptors.forEach((pageDescriptor) => {
            if (outputImagePtr.ref.isLocked(pageDescriptor)) {
                this._deferredVirtualPageHashesToBeFreed.add(pageDescriptor.hash)
            } else {
                nonLockedPageDescriptors.push(pageDescriptor)
            }
        })
        outputImagePtr.ref.freePages(nonLockedPageDescriptors)
    }

    getStats() {
        return this.outputImagePtr.ref.stats
    }

    getLockedPages() {
        return this.outputImagePtr.ref.stats.lockedPages
    }

    freeAllNonLockedPages() {
        const outputImage = this.outputImagePtr.ref
        const pageDescriptorsToFree: PageDescriptor[] = []
        for (const pageUpdateInfo of this._pageUpdateInfosByPageHash.values()) {
            if (!outputImage.isLocked(pageUpdateInfo.pageDescriptor)) {
                pageDescriptorsToFree.push(pageUpdateInfo.pageDescriptor)
            }
        }
        if (pageDescriptorsToFree.length > 0) {
            this.freePages(pageDescriptorsToFree)
        }
    }

    private _outputImagePtr: HalImageVirtualRef | null
    private _subscriptions: Subscription[] = []
    private _pageUpdateInfosByPageHash = new Map<number, PageUpdateInfo>()
    private _deferredVirtualPageHashesToBeFreed = new Set<number>()
    private _imageUpdateScheduler: ImageUpdateScheduler
}

type PageUpdateInfo = {
    pageDescriptor: PageDescriptor
    numRequests: number
    pendingRequest: Promise<void> | null
    // abortController: AbortController
}
